// src/hooks/useRoadWorksData.js
// --- UPDATE: v5.0 — data, filter, live-feed and AI-engine wiring extracted from RoadWorksPage.
// --- UPDATE (v5.1 AI connection)
//   • Network AI insights that reference a road (road_id / asset_id) are attached to that
//     road as `ai_insights`, so table rows, the View modal and the AI panels show them per road.
//   • Live 'asset_prediction_update' events patch the matching road's `ai_rul` in place
//     (marked live) — the table, heatmap and AI Command Center update without a refetch.
//   • Hydraulic 'ai_flood_prediction' events that name a road (road_id / road_ids /
//     affected_roads) tag those roads with `ai_flood_alert` for the row + prediction card.
// --- END UPDATE
// --- UPDATE (v5.2 FlowSense linking + performance)
//   • Listens to the cross-page bus (utils/flowsenseBus): work orders / assets changed on other
//     pages or tabs trigger the same coalesced live refresh.
//   • announceRoadChange(payload) publishes ROAD_CHANGED (and WORK_ORDER_CHANGED when a road
//     work order was raised) so Work Orders + Public Utilities refresh straight away.
//     Our own announcements are ignored here (the page already refetches after a save).
//   • Full work-order lifecycle over the socket (completed / updated / deleted).
//   • WebSocket subscribes ONCE — handlers are read through a ref, so an unstable `logger`
//     no longer tears down and re-subscribes every socket listener on each render.
//   • Live handlers ignore events that arrive after unmount.
// --- END UPDATE
// --- UPDATE (roads v1.4): `pondingRoads` (roads from the live road ponding forecast) are merged onto the roads as
// `ai_ponding` { band, max_depth_m, ponding_segments, first_hours } — the AI prediction, risk index, inventory and
// report tables then explain and rank roads by forecast standing water, without another request.
// --- END UPDATE
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as roadService from '../services/roadService';
import roadAIEngineService from '../services/roadAIEngineService';
import webSocketService from '../services/websocketService';
// --- UPDATE
import { publish as publishBus, subscribe as subscribeBus, BUS_EVENTS } from '../utils/flowsenseBus';
// --- END UPDATE
import { apiFetch, isAuthError } from '../components/roads/page/shared';
// --- UPDATE
import { groupInsightsByRoad, applyLivePrediction } from '../components/roads/roadAiPredictions';
// --- END UPDATE

export const SEARCH_DEBOUNCE_MS = 350;
export const LIVE_REFRESH_COALESCE_MS = 3000;
export const POLL_INTERVAL_MS = 60000;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

const LIVE_UPDATE_EVENTS = [
    'road_update', 
    'work_order_created', 
    'issue_reported', 
    'assessment_recorded',
    'anomaly_clustered',
    // --- UPDATE: full work-order lifecycle (coalesced, so bursts cost one refetch)
    'work_order_completed',
    'work_order_updated',
    'work_order_deleted',
    'maintenance_logged',
    // --- END UPDATE
];

// --- UPDATE: the backend broadcasts work orders on ONE channel with a `type` field
const WO_EVENT_REFRESH_TYPES = new Set(['created', 'status_updated', 'deleted']);
// --- END UPDATE

// --- UPDATE: tag on our own bus messages so this hook skips its own announcements
export const BUS_ORIGIN = 'road-works';
// --- END UPDATE

const isDocHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

export default function useRoadWorksData({ logger, toast, pondingRoads = null }) { // --- UPDATE (roads v1.4): + pondingRoads
    // ── Data ────────────────────────────────────────────────────────────────
    const [analytics, setAnalytics] = useState(null);
    const [liveAiEvents, setLiveAiEvents] = useState([]);
    const [batchInsights, setBatchInsights] = useState([]);
    const [roads, setRoads] = useState([]);
    const [lookups, setLookups] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [totalRoads, setTotalRoads] = useState(0);
    const [totalUnmappedRoads, setTotalUnmappedRoads] = useState(0);
    const [unclusteredCount, setUnclusteredCount] = useState(0);
    const [rawAnomalies, setRawAnomalies] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [aiStatus, setAiStatus] = useState('idle');
    const [fetchError, setFetchError] = useState(null);
    const [aiCircuit, setAiCircuit] = useState(
        () => roadAIEngineService.getDiagnostics?.()?.circuit?.state || 'CLOSED'
    );

    // ── Filters / paging / sort ─────────────────────────────────────────────
    const [search, setSearchRaw] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilterRaw] = useState('');
    const [constituencyFilter, setConstituencyFilterRaw] = useState('');
    const [classificationFilter, setClassificationFilterRaw] = useState('');
    const [conditionFilter, setConditionFilterRaw] = useState('');
    const [showOnlyStarved, setShowOnlyStarvedRaw] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimitRaw] = useState(50);
    const [sort, setSort] = useState({ by: 'name', dir: 'ASC' });
    const sortBy = sort.by;
    const sortDir = sort.dir;
    const [autoRefresh, setAutoRefresh] = useState(true);

    // ── Refs ────────────────────────────────────────────────────────────────
    const isMounted = useRef(true);
    const reqIdRef = useRef(0);
    const aiIdleTimer = useRef(null);
    const liveTimer = useRef(null);
    const pendingWhileHidden = useRef(false);
    const lastFetchAt = useRef(0);
    const authHandled = useRef(false);
    const toastRef = useRef(toast);
    const autoRefreshRef = useRef(autoRefresh);
    toastRef.current = toast;
    autoRefreshRef.current = autoRefresh;
    // --- UPDATE: logger via ref — a new logger object per render can no longer re-create
    // fetchData (which would refetch in a loop) or re-run the effects below
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    // --- END UPDATE

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            clearTimeout(aiIdleTimer.current);
            clearTimeout(liveTimer.current);
        };
    }, []);

    // Filter setters reset to page 1
    const withPageReset = (setter) => (v) => { setter(v); setPage(1); };
    const setters = useMemo(() => ({
        setStatusFilter: withPageReset(setStatusFilterRaw),
        setConstituencyFilter: withPageReset(setConstituencyFilterRaw),
        setClassificationFilter: withPageReset(setClassificationFilterRaw),
        setConditionFilter: withPageReset(setConditionFilterRaw),
        setShowOnlyStarved: withPageReset(setShowOnlyStarvedRaw),
        setLimit: withPageReset(setLimitRaw),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    const setSearch = useCallback((v) => setSearchRaw(v), []);

    // Debounce search
    useEffect(() => {
        if (search === debouncedSearch) return undefined;
        const id = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [search, debouncedSearch]);

    const flushSearch = useCallback(() => {
        setDebouncedSearch(search);
        setPage(1);
    }, [search]);

    const clearFilters = useCallback(() => {
        setSearchRaw(''); setDebouncedSearch('');
        setStatusFilterRaw(''); setConstituencyFilterRaw('');
        setClassificationFilterRaw(''); setConditionFilterRaw('');
        setShowOnlyStarvedRaw(false);
        setPage(1);
    }, []);

    const handleSort = useCallback((col) => {
        setSort(prev => (prev.by === col
            ? { by: col, dir: prev.dir === 'ASC' ? 'DESC' : 'ASC' }
            : { by: col, dir: 'ASC' }));
        setPage(1);
    }, []);

    // ── Centralised auth-expiry logout ──────────────────────────────────────
    const handleAuthExpired = useCallback((source = 'fetch') => {
        const logger = loggerRef.current; // --- UPDATE
        if (authHandled.current) return;
        authHandled.current = true;
        logger.error(`Session token expired (${source}) — initiating auto-logout`);
        toastRef.current?.({
            title: 'Session Expired',
            description: 'Your security token has expired. Logging you out...',
            status: 'warning',
            duration: 3000,
            isClosable: true,
            position: 'top',
        });
        try { localStorage.removeItem('token'); sessionStorage.clear(); } catch (_) {}
        setTimeout(() => { window.location.href = '/auth/sign-in'; }, 1500);
    }, []); // --- UPDATE: logger via ref

    // ── Data loading ────────────────────────────────────────────────────────
    const fetchData = useCallback(async (silent = false) => {
        const logger = loggerRef.current; // --- UPDATE
        const reqId = ++reqIdRef.current;
        logger.perfStart(`fetchData#${reqId}`);
        lastFetchAt.current = Date.now();
        if (!silent) {
            setIsLoading(true);
            setAiStatus('thinking');
        }

        const catchNonAuth = (fallback) => (err) => {
            if (isAuthError(err)) throw err;
            return fallback;
        };

        try {
            const [lookupRes, analyticsRes, roadsRes, aiRes, rulRes, unmappedRes, anomaliesRes] = await Promise.all([
                roadService.getRoadLookups().catch(catchNonAuth({ data: {} })),
                roadService.getRoadAnalyticsOverview().catch(catchNonAuth({ data: null })),
                roadService.getRoadsAdvanced({
                    search: debouncedSearch,
                    status: statusFilter,
                    constituency: constituencyFilter,
                    classification: classificationFilter,
                    condition_rating: conditionFilter,
                    limit,
                    offset: (page - 1) * limit,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    ml_starved: showOnlyStarved ? 'true' : undefined,
                }),
                roadAIEngineService.getNetworkAIInsights().catch(catchNonAuth([])),
                roadAIEngineService.getRoadNetworkRUL().catch(catchNonAuth([])),
                roadService.getRoadsAdvanced({ ml_starved: 'true', limit: 1 }).catch(() => null),
                apiFetch('/roads/telemetry/anomalies').catch(catchNonAuth({ data: [] })),
            ]);

            if (!isMounted.current || reqId !== reqIdRef.current) {
                logger.debug(`Discarded stale response #${reqId}`);
                return;
            }

            if (lookupRes?.data) setLookups(lookupRes.data);
            if (analyticsRes?.data) setAnalytics(analyticsRes.data);
            if (roadsRes?.pagination?.total != null) setTotalRoads(roadsRes.pagination.total);
            if (unmappedRes?.pagination?.total != null) setTotalUnmappedRoads(unmappedRes.pagination.total);

            let mergedRoads = roadsRes?.data || [];
            if (Array.isArray(rulRes) && rulRes.length > 0) {
                const rulMap = new Map(rulRes.map(r => [r.id || r.road_id, r]));
                mergedRoads = mergedRoads.map(road => ({ ...road, ai_rul: rulMap.get(road.id) }));
            }
            // --- UPDATE: per-road AI insights
            const insightsByRoad = groupInsightsByRoad(aiRes);
            if (insightsByRoad.size > 0) {
                mergedRoads = mergedRoads.map(road => {
                    const own = insightsByRoad.get(String(road.id));
                    return own ? { ...road, ai_insights: own } : road;
                });
            }
            // --- END UPDATE
            setRoads(mergedRoads);

            if (aiRes) setBatchInsights(aiRes);

            if (anomaliesRes?.data) {
                setUnclusteredCount(anomaliesRes.data.length);
                setRawAnomalies(anomaliesRes.data);
            }

            setFetchError(null);
            setLastRefresh(new Date());
            setAiStatus('success');
            logger.success('Data fetch complete', {
                roads: roadsRes?.data?.length, total: roadsRes?.pagination?.total,
            });
            logger.perfEnd(`fetchData#${reqId}`);
        } catch (error) {
            if (!isMounted.current || reqId !== reqIdRef.current) return;
            logger.error('Data fetch failed', error);
            setAiStatus('error');

            if (isAuthError(error)) {
                handleAuthExpired('fetch');
                return;
            }

            setFetchError(error);
            if (!silent) {
                toastRef.current?.({
                    title: 'Error fetching data',
                    description: error.message,
                    status: 'error', duration: 3000,
                });
            }
        } finally {
            if (isMounted.current && reqId === reqIdRef.current) {
                setIsLoading(false);
                clearTimeout(aiIdleTimer.current);
                aiIdleTimer.current = setTimeout(() => {
                    if (isMounted.current) setAiStatus('idle');
                }, 2000);
            }
        }
    }, [debouncedSearch, statusFilter, constituencyFilter, classificationFilter, conditionFilter,
        page, limit, sortBy, sortDir, showOnlyStarved, handleAuthExpired]); // --- UPDATE: logger via ref

    const fetchRef = useRef(fetchData);
    fetchRef.current = fetchData;
    const refresh = useCallback((silent = false) => fetchRef.current(silent), []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Coalesced live refresh ──────────────────────────────────────────────
    const scheduleLiveRefresh = useCallback(() => {
        if (!autoRefreshRef.current) return;
        if (isDocHidden()) { pendingWhileHidden.current = true; return; }
        if (liveTimer.current) return;
        liveTimer.current = setTimeout(() => {
            liveTimer.current = null;
            if (isMounted.current && autoRefreshRef.current) refresh(true);
        }, LIVE_REFRESH_COALESCE_MS);
    }, [refresh]);

    // ── Visibility-aware polling ────────────────────────────────────────────
    useEffect(() => {
        if (!autoRefresh) return undefined;
        const logger = loggerRef.current; // --- UPDATE
        const id = setInterval(() => {
            if (isDocHidden()) return;
            refresh(true);
        }, POLL_INTERVAL_MS);
        const onVisible = () => {
            if (isDocHidden()) return;
            const overdue = Date.now() - lastFetchAt.current > POLL_INTERVAL_MS;
            if (pendingWhileHidden.current || overdue) {
                pendingWhileHidden.current = false;
                logger.info('Tab visible again — catching up');
                refresh(true);
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [autoRefresh, refresh]); // --- UPDATE: logger via ref

    // ── Live AI event handlers (stable) ─────────────────────────────────────
    const handleMLAnomaly = useCallback((data = {}) => {
        const logger = loggerRef.current; // --- UPDATE
        if (!isMounted.current) return; // --- UPDATE
        logger.warn('AI Anomaly Detected', data);

        const isFlood = data.level === 'HIGH' || data.level === 'EMPTY';
        const title = isFlood ? '🚨 FLOOD PROTOCOL ACTIVATED' : '⚠️ SENSOR ANOMALY DETECTED';
        const conf = Number(data.confidence);
        const message = isFlood
            ? `Water sensor ${data.device_id} detected CRITICAL volume. FlowSense ML has automatically evaluated nearby road elevations and closed surrounding segments.`
            : `Unusual telemetry spike detected by Isolation Forest model on sensor ${data.sensor_id}. Confidence: ${Number.isFinite(conf) ? (conf * 100).toFixed(1) : '—'}%.`;

        setLiveAiEvents(prev => [{
            id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: 'live',
            severity: 'critical',
            title,
            message,
            recommendations: ['Review auto-dispatched drainage crews', 'Verify road closure on GIS map'],
            timestamp: new Date().toISOString(),
            isLive: true,
            model: isFlood ? 'insights' : 'iforest',
            confidence: Number.isFinite(conf) ? conf : undefined,
        }, ...prev].slice(0, 10));

        toastRef.current?.({
            title: 'Critical AI Intervention',
            description: message,
            status: 'error',
            duration: 6000,
            isClosable: true,
            position: 'top-right',
        });
    }, []); // --- UPDATE: logger via ref

    const handlePredictiveRiskUpdate = useCallback((data = {}) => {
        const logger = loggerRef.current; // --- UPDATE
        if (!isMounted.current) return; // --- UPDATE
        logger.warn('Live ML Prediction Received', data);
        // --- UPDATE: patch the matching road immediately
        const targetId = data.assetId ?? data.road_id ?? data.roadId;
        if (targetId != null) {
            setRoads(prev => {
                let hit = false;
                const next = prev.map(r => {
                    if (String(r.id) !== String(targetId) && String(r.asset_id ?? '') !== String(targetId)) return r;
                    hit = true;
                    return applyLivePrediction(r, data);
                });
                return hit ? next : prev;
            });
        }
        // --- END UPDATE
        setLiveAiEvents(prev => [{
            id: `live-rul-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: 'live',
            severity: data.confidence > 80 ? 'critical' : 'high',
            title: `Predictive Risk Alert: Asset ${data.assetId}`,
            message: `ML model projects critical degradation. Prescriptive Action: ${data.action} (Confidence: ${data.confidence}%)`,
            actions: [{
                label: 'Review & Dispatch',
                intent: 'OPEN_QUICK_WO',
                payload: { assetId: data.assetId, priority: 'critical', category: 'Road Paving', title: `${data.action} Required` },
            }],
            timestamp: new Date().toISOString(),
            isLive: true,
            model: 'rsf',
            confidence: data.confidence,
        }, ...prev].slice(0, 10));

        toastRef.current?.({
            title: 'Live RUL Degradation Detected',
            description: `Asset ${data.assetId} requires ${data.action}.`,
            status: 'warning',
            duration: 6000,
            isClosable: true,
            position: 'top-right',
        });
    }, []); // --- UPDATE: logger via ref

    // Dedicated handler for hydraulic flood broadcasts linked to road baselines
    const handleAiFloodPrediction = useCallback((data = {}) => {
        const logger = loggerRef.current; // --- UPDATE
        if (!isMounted.current || !data || !data.well_id) return; // --- UPDATE: mount guard
        const severity = (data.predictedSeverity || data.flood_risk || 'LOW').toUpperCase();
        if (severity !== 'HIGH' && severity !== 'CRITICAL') return;

        logger.warn('[useRoadWorksData] Hydraulic AI Flood Prediction received', data);
        const floodProb = Math.round((data.floodRiskProbability || data.flood_probability || 0.8) * 100);

        // --- UPDATE: tag roads the hydraulic model names as affected
        const affected = [data.road_id, ...(Array.isArray(data.road_ids) ? data.road_ids : []),
            ...(Array.isArray(data.affected_roads) ? data.affected_roads.map(r => (r && typeof r === 'object' ? r.id ?? r.road_id : r)) : [])]
            .filter(v => v !== undefined && v !== null).map(String);
        if (affected.length) {
            const alert = { severity, probability: floodProb, well_id: data.well_id, well_name: data.wellName || null, at: new Date().toISOString() };
            setRoads(prev => {
                let hit = false;
                const next = prev.map(r => {
                    if (!affected.includes(String(r.id))) return r;
                    hit = true;
                    return { ...r, ai_flood_alert: alert };
                });
                return hit ? next : prev;
            });
        }
        // --- END UPDATE

        setLiveAiEvents(prev => [{
            id: `flood-ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: 'live',
            severity: 'critical',
            isLive: true,
            model: 'insights',
            confidence: (data.floodRiskProbability || data.flood_probability || 0.8),
            title: `Hydraulic Surcharge: Well ${data.wellName || data.well_id}`,
            message: `Hydrological AI estimates ${floodProb}% flood risk. Water accumulation within road sub-base accelerates surface cracking and pothole formation.`,
            recommendations: [
                'Inspect sub-base drainage outfalls along corridor',
                'Pre-stage vac-truck for rapid sump dewatering'
            ],
            actions: [{
                label: 'Dispatch Drainage Crew',
                intent: 'OPEN_QUICK_WO',
                payload: {
                    assetId: data.well_id,
                    priority: 'critical',
                    category: 'Drainage',
                    title: `Emergency Drainage: Near ${data.wellName || data.well_id}`
                }
            }, {
                label: 'View Hazard on Map',
                intent: 'HIGHLIGHT_MAP',
                payload: { assetId: data.well_id }
            }],
            timestamp: new Date().toISOString()
        }, ...prev].slice(0, 10));

        scheduleLiveRefresh();
    }, [scheduleLiveRefresh]); // --- UPDATE: logger via ref

    // ── WebSocket — subscribe ONCE ─────────────────────────────────────────
    // --- UPDATE: latest handlers via ref → the socket effect below never re-subscribes
    const liveRef = useRef({});
    liveRef.current = { logger, scheduleLiveRefresh, handleMLAnomaly, handlePredictiveRiskUpdate, handleAiFloodPrediction };
    // --- END UPDATE
    useEffect(() => {
        webSocketService.connect();

        // --- UPDATE: thin stable wrappers that forward to the latest handlers
        const handleConnect = () => { liveRef.current.logger.info('WebSocket Connected'); };
        const handleDisconnect = () => { liveRef.current.logger.warn('WebSocket Disconnected'); };
        const handleLiveUpdate = (message) => {
            liveRef.current.logger.info('Live Event Received', message);
            liveRef.current.scheduleLiveRefresh();
        };
        const handleMLAnomaly = (data) => liveRef.current.handleMLAnomaly(data);
        const handlePredictiveRiskUpdate = (data) => liveRef.current.handlePredictiveRiskUpdate(data);
        const handleAiFloodPrediction = (data) => liveRef.current.handleAiFloodPrediction(data);
        // --- END UPDATE

        // --- UPDATE
        const handleWorkOrderEvent = (data) => {
            if (WO_EVENT_REFRESH_TYPES.has(data?.type)) handleLiveUpdate(data);
        };
        // --- END UPDATE

        const handleWaterLevel = (data) => {
            if (data && (data.level === 'HIGH' || data.level === 'EMPTY')) handleMLAnomaly(data);
        };

        webSocketService.on('connect', handleConnect);
        webSocketService.on('disconnect', handleDisconnect);
        LIVE_UPDATE_EVENTS.forEach(evt => webSocketService.subscribe(evt, handleLiveUpdate));
        webSocketService.subscribe('anomaly_recorded', handleMLAnomaly);
        webSocketService.subscribe('work_order_events', handleWorkOrderEvent); // --- UPDATE
        webSocketService.subscribe('new_water_level_reading', handleWaterLevel);
        webSocketService.subscribe('asset_prediction_update', handlePredictiveRiskUpdate);
        webSocketService.subscribe('ai_flood_prediction', handleAiFloodPrediction);

        return () => {
            webSocketService.off('connect', handleConnect);
            webSocketService.off('disconnect', handleDisconnect);
            LIVE_UPDATE_EVENTS.forEach(evt => webSocketService.unsubscribe(evt, handleLiveUpdate));
            webSocketService.unsubscribe('anomaly_recorded', handleMLAnomaly);
            webSocketService.unsubscribe('work_order_events', handleWorkOrderEvent); // --- UPDATE
            webSocketService.unsubscribe('new_water_level_reading', handleWaterLevel);
            webSocketService.unsubscribe('asset_prediction_update', handlePredictiveRiskUpdate);
            webSocketService.unsubscribe('ai_flood_prediction', handleAiFloodPrediction);
        };
    }, []); // --- UPDATE: subscribe once (handlers read through liveRef)

    // --- UPDATE: FlowSense cross-page bus ───────────────────────────────────
    useEffect(() => {
        const off = subscribeBus((type, payload = {}) => {
            if (payload.origin === BUS_ORIGIN) return; // our own announcement — page already refetched
            if (type === BUS_EVENTS.WORK_ORDER_CHANGED || type === BUS_EVENTS.ASSET_CHANGED || type === BUS_EVENTS.ROAD_CHANGED) {
                liveRef.current.logger.info('FlowSense bus event', { type, payload });
                liveRef.current.scheduleLiveRefresh();
            }
        });
        return off;
    }, []);

    /**
     * Tell the rest of FlowSense a road changed. Call after a successful save.
     *   announceRoadChange({ id, action: 'updated' | 'created' | 'deleted' | 'status' | 'assessment'
     *                        | 'inspection' | 'issue' | 'work_order' | 'mapped', status?, asset_id? })
     * action 'work_order' / 'issue' also publishes WORK_ORDER_CHANGED (the road raised a job).
     */
    const announceRoadChange = useCallback((payload = {}) => {
        const msg = { ...payload, origin: BUS_ORIGIN };
        publishBus(BUS_EVENTS.ROAD_CHANGED, msg);
        if (payload.action === 'work_order' || payload.action === 'issue') {
            publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: 'created', road_id: payload.id ?? null, asset_id: payload.asset_id ?? null, origin: BUS_ORIGIN });
        }
    }, []);
    // --- END UPDATE

    // ── Road AI Engine event bus ────────────────────────────────────────────
    useEffect(() => {
        const logger = loggerRef.current; // --- UPDATE
        const t = (opts) => toastRef.current?.(opts);
        const subs = [
            roadAIEngineService.on('circuit:open', () => {
                logger.error('AI Engine Circuit OPEN');
                if (isMounted.current) setAiCircuit('OPEN');
                t({ title: '⚠️ AI Engine Unreachable', description: 'The Circuit Breaker has opened to protect the system. Falling back to cached data.', status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
            }),
            roadAIEngineService.on('circuit:closed', () => {
                logger.success('AI Engine Circuit CLOSED — restored');
                if (isMounted.current) setAiCircuit('CLOSED');
                t({ title: '✅ AI Engine Restored', description: 'Connection to FlowSense Machine Learning modules re-established.', status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
            }),
            roadAIEngineService.on('offline:queued', ({ count } = {}) => {
                logger.warn('Request queued offline', { count });
                t({ title: '📡 Offline Mode', description: `Request saved locally. ${count} item(s) pending sync when connection restores.`, status: 'warning', duration: 4000, isClosable: true, position: 'bottom-right' });
            }),
            roadAIEngineService.on('offline:flushed', () => {
                logger.success('Offline queue flushed');
                t({ title: '☁️ Sync Complete', description: 'Offline queued actions have been successfully pushed to the server.', status: 'success', duration: 3000, isClosable: true, position: 'bottom-right' });
            }),
            roadAIEngineService.on('request:error', ({ error } = {}) => {
                if (isAuthError(error)) handleAuthExpired('ai-engine');
            }),
        ];
        return () => {
            subs.forEach(unsub => { try { typeof unsub === 'function' && unsub(); } catch (_) {} });
            roadAIEngineService.cancelAll?.();
        };
    }, [handleAuthExpired]); // --- UPDATE: logger via ref

    const dismissLiveEvent = useCallback((id) => {
        setLiveAiEvents(prev => prev.filter(e => e.id !== id));
    }, []);

    // --- UPDATE (roads v1.4): forecast standing water per road
    const roadsOut = useMemo(() => {
        if (!Array.isArray(pondingRoads) || !pondingRoads.length) return roads;
        const byId = new Map(pondingRoads.map((r) => [String(r.road_id), r]));
        let hit = false;
        const next = roads.map((r) => {
            const p = byId.get(String(r.id));
            if (!p && !r.ai_ponding) return r;
            hit = true;
            return p ? { ...r, ai_ponding: { band: p.band, max_depth_m: p.max_depth_m, ponding_segments: p.ponding_segments, first_hours: p.first_hours } } : { ...r, ai_ponding: undefined };
        });
        return hit ? next : roads;
    }, [roads, pondingRoads]);
    // --- END UPDATE

    const hasActiveFilters = Boolean(
        search || statusFilter || constituencyFilter || classificationFilter || conditionFilter || showOnlyStarved
    );

    return {
        // data
        analytics, liveAiEvents, batchInsights, roads: roadsOut, lookups, isLoading, totalRoads, // --- UPDATE (roads v1.4): + ai_ponding
        totalUnmappedRoads, unclusteredCount, rawAnomalies, lastRefresh, aiStatus,
        fetchError, aiCircuit,
        // filters
        search, setSearch, debouncedSearch, flushSearch,
        statusFilter, constituencyFilter, classificationFilter, conditionFilter, showOnlyStarved,
        ...setters,
        clearFilters, hasActiveFilters,
        page, setPage, limit, sortBy, sortDir, handleSort,
        autoRefresh, setAutoRefresh,
        // actions
        fetchData: refresh, scheduleLiveRefresh, handleAuthExpired, dismissLiveEvent,
        announceRoadChange, // --- UPDATE
        isSearchPending: search !== debouncedSearch,
    };
}